import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { GetFileTypeResult, GetPossessionTitlesResult, SetupResultAlt } from '../../../../../shared/services/pcode/bonus/bonus.models';
import { BonusSharedService } from '../../../../../shared/services/pcode/bonus/bonus.service';
import { EditFileTypeConfigurationDialogInput, FileTypeGroups } from '../../companies.models';
import { CompaniesService } from '../../companies.service';

@Component({
    selector: 'edit-filetype-company-association-dialog',
    templateUrl: './edit-filetype-config-dialog.component.html',
    styleUrls: ['./edit-filetype-config-dialog.component.scss'],
    standalone: false
})
export class EditFileConfigurationDialogComponent implements OnInit
{
	_personTypes: Array<SetupResultAlt>;
	_beneficiaryTypes: Array<SetupResultAlt>;
	_possessionTitles: Array<GetPossessionTitlesResult>;
	_fileType: GetFileTypeResult;
	_fileTypes: Array<GetFileTypeResult>;

	_fileTypeGroups: Array<FileTypeGroups>;

	// constructor
	constructor
		(
			private dialogRef: MatDialogRef<EditFileConfigurationDialogComponent>,
			@Inject(MAT_DIALOG_DATA) public model: EditFileTypeConfigurationDialogInput,
			private fb: UntypedFormBuilder,
			private companiesService: CompaniesService,
			private bonusSharedService: BonusSharedService
		)
	{
	}

	public _form: UntypedFormGroup = this.fb.group(
		{
			companyId: [null, [Validators.required]],
			bonusTypeId: [null, [Validators.required]],
			scopeTypeId: [null, [Validators.required]],

			fileTypeId: [null, [Validators.required]],
			position: [null],

			personTypeId: [null],
			beneficiaryTypeId: [null],
			possessionTitleId: [null],
			mandatory: [true]
		});

	get fileTypeId() { return this._form.get('fileTypeId'); }
	get position() { return this._form.get('position'); }

	// on init
	ngOnInit()
	{
		// patching initial values
		this.position.patchValue(this.model.position)

		// observables
		const _personTypesObs = this.bonusSharedService.getPersonTypes();
		const _beneficiaryTypesObs = this.bonusSharedService.getBeneficiaryTypes();
		const _possessionTitlesObs = this.bonusSharedService.getPossessionTitles();
		const _fileTypeObs = this.bonusSharedService.getFileType(this.model.fileTypeId);
		const _fileTypesObs = this.bonusSharedService.getFileTypes();

		// edit configuration
		if (this.model.fileTypeId)
		{
			forkJoin([_personTypesObs, _beneficiaryTypesObs, _possessionTitlesObs, _fileTypeObs]).subscribe(results =>
			{
				this._personTypes = results[0];
				this._beneficiaryTypes = results[1];
				this._possessionTitles = results[2];
				this._fileType = results[3];

				// base info patch
				this._form.patchValue(this.model);
			});
		}

		// new file type association
		else
		{
			this.position.setValidators([Validators.required]);

			forkJoin([_personTypesObs, _beneficiaryTypesObs, _possessionTitlesObs, _fileTypesObs]).subscribe(results =>
			{
				this._personTypes = results[0];
				this._beneficiaryTypes = results[1];
				this._possessionTitles = results[2];
				this._fileTypes = results[3];

				// organize file types in groups
				this.organizeFileTypes();

				// base info patch
				this._form.patchValue(this.model);
			});
		}
	}

	// organize file types in groups
	organizeFileTypes()
	{
		// file types groups
		let _groups = this._fileTypes.map(value =>
		{
			return {
				id: value.fileGroupId,
				name: value.fileGroupName,
				label: value.fileGroupLabel,
				position: value.fileGroupPosition,
				fileTypes: []
			}
		});

		// distinct + sort
		_groups = _groups
			.filter((thing, i, arr) => arr.findIndex(t => t.id === thing.id) === i)
			.sort((a, b) => (a.position > b.position) ? 1 : ((b.position > a.position) ? -1 : 0));

		// file types by group
		_groups.forEach(group =>
		{
			group.fileTypes = this._fileTypes.filter(x =>
				x.fileGroupId === group.id &&
				this.model.excludeFileTypes.indexOf(x.id) < 0 // check: already associated file types
			)
		});

		this._fileTypeGroups = _groups;
	}

	// submit
	onSubmit()
	{
		this._form.markAllAsTouched();

		if (this._form.valid)
		{
			this.companiesService.insertFileTypeConfiguration(this._form.value).subscribe(() =>
			{
				this.dialogRef.close({ updated: true });
			})
		}
	}
}
